import { BadgeAlert, RotateCw } from 'lucide-react';

import { hasRiderRole } from '@/api/types';
import { useAuth } from '@/auth/auth-context';
import { BrandLockup } from '@/components/brand-logo';
import { Button } from '@/components/ui/button';

/**
 * Signed in, with no rider profile behind the account.
 *
 * A real state, not an error, and one only the platform can fix. The two
 * halves are granted in two separate admin actions — the RIDER role, then a
 * `riders` row with a vehicle on it — so the fix differs depending on which is
 * missing, and this says which rather than showing an empty run sheet that
 * will never fill.
 */
export function NotARiderScreen({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  const { user, signOut } = useAuth();
  const hasRole = hasRiderRole(user);

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <header className="flex h-header items-center px-gutter">
        <BrandLockup size={26} />
      </header>

      <main
        id="main"
        className="mx-auto flex w-full max-w-app flex-1 flex-col justify-center px-gutter pb-10"
      >
        <span className="mb-5 grid size-14 place-items-center rounded-2xl bg-muted text-ink-muted">
          <BadgeAlert className="size-7" aria-hidden="true" />
        </span>

        <h1 className="text-2xl font-bold tracking-tight text-ink">
          This number is not set up to ride yet
        </h1>

        <p className="mt-3 text-[0.9375rem] leading-normal text-ink-muted">
          {hasRole
            ? 'You have the rider role, but no vehicle has been registered against your account. Ask the office to add you to the rider roster.'
            : `Signed in as ${user?.phone ?? 'this number'}. Ask the office to add you as a rider, then check again.`}
        </p>

        <div className="mt-8 grid gap-2">
          <Button size="lg" block onClick={onRetry} loading={retrying} loadingLabel="Checking">
            <RotateCw aria-hidden="true" />
            Check again
          </Button>
          <Button variant="ghost" size="md" block onClick={() => void signOut()}>
            Use a different number
          </Button>
        </div>
      </main>
    </div>
  );
}
